import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { PanelNotification } from '../../context/user.context';
import StyledIcon from '../styled-icon';
import { icons } from '../../icons';

type TopPanelNotificationsProps = {
  notifications: PanelNotification[];
  interval?: number;
};

const DEFAULT_INTERVAL = 5000;

const TopPanelNotifications: React.FC<TopPanelNotificationsProps> = ({
  notifications,
  interval = DEFAULT_INTERVAL,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState<1 | -1>(1);
  const prevIndexRef = useRef(0);
  const hasMultiple = notifications.length > 1;

  useEffect(() => {
    setActiveIndex(0);
    prevIndexRef.current = 0;
    setDirection(1);
  }, [notifications]);

  useEffect(() => {
    if (!hasMultiple || isPaused) {
      return;
    }

    const rotate = window.setInterval(() => {
      setActiveIndex((prev) => {
        const total = notifications.length;
        if (total <= 1) {
          return prev;
        }
        const next = (prev + 1) % total;
        setDirection(1);
        prevIndexRef.current = next;
        return next;
      });
    }, interval);

    return () => window.clearInterval(rotate);
  }, [interval, notifications.length, hasMultiple, isPaused]);

  const current = useMemo(
    () => notifications[activeIndex],
    [notifications, activeIndex]
  );

  const handlePrev = useCallback(() => {
    if (!hasMultiple) {
      return;
    }
    setDirection(-1);
    const total = notifications.length;
    const nextIndex = (activeIndex - 1 + total) % total;
    prevIndexRef.current = nextIndex;
    setActiveIndex(nextIndex);
  }, [activeIndex, notifications.length, hasMultiple]);

  const handleNext = useCallback(() => {
    if (!hasMultiple) {
      return;
    }
    setDirection(1);
    const total = notifications.length;
    const nextIndex = (activeIndex + 1) % total;
    prevIndexRef.current = nextIndex;
    setActiveIndex(nextIndex);
  }, [activeIndex, notifications.length, hasMultiple]);

  const handleDotClick = useCallback(
    (index: number) => {
      if (index === activeIndex) {
        return;
      }
      const total = notifications.length;
      const forward =
        (activeIndex === total - 1 && index === 0) ||
        (index > activeIndex && !(activeIndex === 0 && index === total - 1));
      setDirection(forward ? 1 : -1);
      prevIndexRef.current = index;
      setActiveIndex(index);
    },
    [activeIndex, notifications.length]
  );

  if (!current) {
    return null;
  }

  return (
    <Wrapper
      backgroundColor={current.top_line_background_color}
      textColor={current.top_line_text_color}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      {hasMultiple ? (
        <ControlButton
          type="button"
          aria-label="Предыдущее уведомление"
          onClick={handlePrev}
        >
          <StyledIcon src={icons.ArrowLeft}/>
        </ControlButton>
      ) : null}
      <Viewport>
        <AnimatedCard key={current.id ?? activeIndex} direction={direction}>
          <Content>
            {current.title ? <Title>{current.title}</Title> : null}
            {current.body ? (
              <Body dangerouslySetInnerHTML={{ __html: current.body }} />
            ) : null}
          </Content>
        </AnimatedCard>
      </Viewport>
      {hasMultiple ? (
        <ControlsArea>
          <ControlButton
            type="button"
            aria-label="Следующее уведомление"
            onClick={handleNext}
          >
            <StyledIcon src={icons.ArrowRight}/>
          </ControlButton>
          <Dots>
            {notifications.map((notification, index) => (
              <Dot
                key={notification.id ?? index}
                type="button"
                aria-label={`Перейти к уведомлению ${index + 1}`}
                aria-pressed={index === activeIndex}
                $isActive={index === activeIndex}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </Dots>
        </ControlsArea>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'backgroundColor' && prop !== 'textColor',
})<{
  backgroundColor?: string;
  textColor?: string;
}>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
  padding: 12px 24px;
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor || theme.palette[10]};
  color: ${({ textColor, theme }) => textColor || theme.palette[900]};
  border-bottom: ${({theme}) => `1px solid ${theme.palette[20]}`};
  box-sizing: border-box;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    box-shadow: ${({theme}) => theme.shadows.menu};
    transform: translateY(-1px);
  }
`;

const Viewport = styled.div`
  flex: 1;
  overflow: hidden;
  min-height: 52px;
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translate3d(24px, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translate3d(-24px, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const AnimatedCard = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'direction',
})<{direction: 1 | -1}>`
  width: 100%;
  display: flex;
  align-items: center;
  animation: ${({direction}) =>
      direction === 1 ? slideInRight : slideInLeft}
    0.4s ease-out both;
  will-change: transform, opacity;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 16px;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
`;

const Body = styled.div`
  font-size: 13px;
  line-height: 1.4;
  word-break: break-word;

  p {
    margin: 0;
  }
`;

const ControlsArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid currentColor;
  border-radius: 50%;
  color: inherit;
  cursor: pointer;
  transition: transform 0.18s ease, background 0.18s ease, opacity 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.75;
  }

  &:active {
    transform: scale(0.94);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  svg path {
    stroke: currentColor;
  }
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
`;

const Dot = styled.button<{$isActive: boolean}>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: currentColor;
  opacity: ${({ $isActive }) => ($isActive ? 0.75 : 0.25)};
  transition: transform 0.18s ease, opacity 0.18s ease;
  cursor: pointer;
  padding: 0;

  &:hover {
    transform: scale(1.25);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    opacity: 1;
  }
`;

export default TopPanelNotifications;
